import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTracker } from '@/lib/analytics/provider';
import { isMobile } from '@/utils/isMobile';

/**
 * Tracks page visit events with URL and platform information
 *
 * @param trackEvent - The track function from useTracker hook
 * @param url - Full URL including origin, pathname and search params
 * @param platform - Platform type (Mobile or Desktop)
 */
const pageVisitedTracking = (
	trackEvent: ReturnType<typeof useTracker>['track'],
	{ url, platform }: { url: string; platform: 'Mobile' | 'Desktop' },
) => {
	try {
		trackEvent('Page_Visited', {
			Page_URL: url,
			Platform: platform,
		});
	} catch (error) {
		// Handle any errors gracefully to avoid breaking the app
		console.error('Error tracking page visit:', error);
	}
};

/**
 * RouteChangeTracker component tracks page visits whenever the route changes.
 *
 * This component should be placed in the root layout to track all route changes
 * across the application, similar to how it would be done in Next.js App Router's layout.tsx
 *
 */
export function RouteChangeTracker() {
	const router = useRouterState();
	const { track: trackEvent } = useTracker();
	const pathname = router.location.pathname;
	const searchParams = router.location.search;

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally tracking only pathname changes, not search params or trackEvent (matches Next.js implementation)
	useEffect(() => {
		try {
			const originName =
				typeof window !== 'undefined' ? window.location.origin : '';
			const searchString =
				Object.keys(searchParams).length > 0
					? `?${new URLSearchParams(searchParams as Record<string, string>).toString()}`
					: '';
			const url = `${originName}${pathname}${searchString}`;

			pageVisitedTracking(trackEvent, {
				url,
				platform: isMobile() ? 'Mobile' : 'Desktop',
			});
		} catch (error) {
			console.error('Error in Page Visit Tracking trackData:', error);
		}
	}, [pathname]);

	// This component doesn't render anything
	return null;
}
